import React from 'react';
import { Star, MapPin, ExternalLink, ChevronRight } from 'lucide-react';
import { getImageUrl } from '../utils/imageUrl';

interface Hotel {
  name: string;
  stars: number;
  image: string;
  address: string;
  amenities: string[];
  meals: string[];
  bookingUrl?: string;
}

interface Formula {
  id: number;
  name: string;
  priceType: 'PER_PERSON' | 'PER_ROOM';
  perPersonPrice: number | null;
  roomPrices: {
    single: number;
    double: number;
    triple: number;
    quadruple: number;
    quintuple: number;
  };
  discountPercent?: number | null;
  hotels: Hotel[];
}

interface PackageFormulasProps {
  formulas: Formula[];
  selectedFormulaId: number | undefined;
  onSelectFormula: (formula: Formula) => void;
  /** Si fourni, un bouton « Réserver » apparaît sur chaque formule et appelle
   *  cette fonction (qui doit pré-remplir et ouvrir le modal de réservation). */
  onReserve?: (formula: Formula) => void;
  theme: {
    primaryColor: string;
    secondaryColor: string;
    textColor: string;
  };
}

function getDisplayPrice(formula: Formula) {
  if (formula.priceType === 'PER_PERSON' && formula.perPersonPrice) {
    return formula.perPersonPrice;
  }
  
  if (formula.roomPrices) {
    const prices = Object.values(formula.roomPrices).filter(price => price > 0);
    return prices.length > 0 ? Math.min(...prices) : 0;
  }

  return 0;
}

function formatPrice(formula: Formula) {
  if (formula.priceType === 'PER_PERSON') {
    const price = formula.perPersonPrice || 0;
    const formattedPrice = price.toLocaleString('fr-DZ');
    return `${formattedPrice} DA par personne`;
  } else {
    // Prix par personne selon le type de chambre
    const prices = Object.values(formula.roomPrices).filter(price => price > 0);
    
    if (prices.length === 0) {
      return 'Prix sur demande';
    }

    const lowestPrice = Math.min(...prices);
    return `À partir de ${lowestPrice.toLocaleString('fr-DZ')} DA par personne`;
  }
}

// Fonction pour afficher les détails des prix par personne selon le type de chambre
function getRoomPricesDetails(formula: Formula) {
  if (formula.priceType !== 'PER_ROOM') return null;

  return Object.entries(formula.roomPrices)
    .filter(([_, price]) => price > 0)
    .map(([type, price]) => {
      const discountedPrice = formula.discountPercent 
        ? price * (1 - formula.discountPercent / 100)
        : price;

      return {
        type,
        label: {
          single: '1 personne',
          double: '2 personnes',
          triple: '3 personnes',
          quadruple: '4 personnes',
          quintuple: '5 personnes'
        }[type],
        capacity: {
          single: 1,
          double: 2,
          triple: 3,
          quadruple: 4,
          quintuple: 5
        }[type],
        price,
        discountedPrice
      };
    });
}

// Fonction utilitaire pour générer les étoiles
function HotelStars({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star 
          key={i}
          className={`w-4 h-4 ${i < count ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
        />
      ))}
      <span className="ml-1 text-sm text-gray-600">{count} étoiles</span>
    </div>
  );
}

function HotelCard({ hotel, theme }: { hotel: Hotel; theme: PackageFormulasProps['theme'] }) {
  const bookingUrl = hotel.bookingUrl?.trim();
  const isValidUrl = bookingUrl && (bookingUrl.startsWith('http://') || bookingUrl.startsWith('https://'));

  return (
    <div className="border border-gray-100 rounded-lg p-3 bg-white">
      <div className="flex gap-4">
        {hotel.image && (
          <img
            src={getImageUrl(hotel.image)}
            alt={hotel.name}
            className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
            <p className="font-medium text-gray-800 break-words">{hotel.name}</p>
            <HotelStars count={Number(hotel.stars) || 0} />
          </div>
          {hotel.address && (
            <div className="flex items-start text-sm text-gray-500">
              <MapPin className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
              <span className="break-words">{hotel.address}</span>
            </div>
          )}
          {isValidUrl && (
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors hover:bg-gray-50"
              style={{
                borderColor: theme.primaryColor,
                color: theme.primaryColor,
              }}
              title="Visiter le site de l'hôtel"
              aria-label="Visiter le site de l'hôtel"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Visiter le site de l'hôtel
            </a>
          )}
        </div>
      </div>

      {hotel.amenities.length > 0 && (
        <div className="mt-2">
          <div className="flex flex-wrap gap-1">
            {hotel.amenities.map((amenity, index) => (
              <span 
                key={index}
                className="px-2 py-0.5 text-xs rounded-full"
                style={{ 
                  backgroundColor: `${theme.primaryColor}20`,
                  color: theme.primaryColor 
                }}
              >
                {amenity}
              </span>
            ))}
          </div>
        </div>
      )}

      {hotel.meals.length > 0 && (
        <div className="mt-2">
          <div className="flex flex-wrap gap-1">
            {hotel.meals.map((meal, index) => (
              <span 
                key={index}
                className="px-2 py-0.5 text-xs rounded-full"
                style={{ 
                  backgroundColor: `${theme.secondaryColor}20`,
                  color: theme.secondaryColor 
                }}
              >
                {meal}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PackageFormulas({
  formulas,
  selectedFormulaId,
  onSelectFormula,
  onReserve,
  theme,
}: PackageFormulasProps) {
  return (
    <div className="grid grid-cols-1 gap-5">
      {formulas.map((formula) => {
        const hotels = formula.hotels?.length ? formula.hotels : ((formula as any).hotel ? [(formula as any).hotel] : []);

        return (
          <div
            key={formula.id}
            className={`relative border-2 rounded-2xl p-5 sm:p-6 cursor-pointer transition-all overflow-hidden ${
              selectedFormulaId === formula.id
                ? 'shadow-xl scale-[1.005]'
                : 'hover:shadow-lg hover:scale-[1.005]'
            }`}
            style={{
              borderColor: selectedFormulaId === formula.id ? theme.primaryColor : '#e5e7eb',
              backgroundColor: selectedFormulaId === formula.id ? `${theme.primaryColor}05` : '#ffffff',
            }}
            onClick={() => onSelectFormula(formula)}
          >
            {/* Indicateur "sélectionné" */}
            {selectedFormulaId === formula.id && (
              <div
                className="absolute top-0 right-0 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white rounded-bl-xl"
                style={{ backgroundColor: theme.primaryColor }}
              >
                Sélectionnée
              </div>
            )}

            {/* En-tête de la formule */}
            <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Formule</div>
                <h4 className="text-xl font-bold break-words text-gray-900">{formula.name}</h4>
              </div>
              <div className="text-right shrink-0">
                {formula.discountPercent && formula.discountPercent > 0 && (
                  <div className="flex items-center gap-2 justify-end mb-1">
                    <span className="text-gray-400 line-through text-sm">
                      {formatPrice(formula)}
                    </span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-rose-100 text-rose-700">
                      -{formula.discountPercent}%
                    </span>
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                  à partir de
                </div>
                <div className="text-2xl font-extrabold tabular-nums" style={{ color: theme.primaryColor }}>
                  {formatPrice({
                    ...formula,
                    perPersonPrice: formula.perPersonPrice
                      ? formula.perPersonPrice * (1 - (formula.discountPercent || 0) / 100)
                      : null,
                    roomPrices: Object.entries(formula.roomPrices).reduce((acc, [key, value]) => ({
                      ...acc,
                      [key]: value * (1 - (formula.discountPercent || 0) / 100)
                    }), {} as Formula['roomPrices'])
                  })}
                </div>
              </div>
            </div>

            {/* Hôtels — disposition horizontale sur grand écran */}
            <div className="space-y-4">
              {hotels.length > 0 && (
                <div>
                  <h5 className="font-medium mb-2">
                    {hotels.length === 1 ? 'Hôtel' : 'Hôtels'}
                  </h5>
                  <div
                    className={
                      hotels.length > 1
                        ? 'grid grid-cols-1 lg:grid-cols-2 gap-3'
                        : 'space-y-2'
                    }
                  >
                    {hotels.map((hotel, idx) => (
                      <HotelCard key={idx} hotel={hotel} theme={theme} />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Prix des chambres */}
              {formula.priceType === 'PER_ROOM' && getRoomPricesDetails(formula)?.length > 0 && (
                <div>
                  <h5 className="font-medium mb-2">Prix par personne selon le type de chambre</h5>
                  <div className="flex flex-wrap gap-2">
                    {getRoomPricesDetails(formula)?.map(room => (
                      <div
                        key={room.type}
                        className="px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5"
                        style={{
                          backgroundColor: `${theme.primaryColor}10`,
                          color: theme.primaryColor
                        }}
                      >
                        <span className="font-medium">
                          Chambre {room.label}
                        </span>
                        {formula.discountPercent && formula.discountPercent > 0 ? (
                          <div className="flex items-center gap-1">
                            <span className="line-through text-gray-500">
                              {room.price.toLocaleString('fr-DZ')} DA
                            </span>
                            <span className="font-semibold">
                              {Math.round(room.discountedPrice).toLocaleString('fr-DZ')} DA / pers.
                            </span>
                          </div>
                        ) : (
                          <span className="font-semibold">
                            {room.price.toLocaleString('fr-DZ')} DA / pers.
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Bouton « Réserver cette formule » — mis en évidence */}
            {onReserve && (
              <div className="mt-5 pt-4 border-t-2 border-dashed border-gray-200">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectFormula(formula);
                    onReserve(formula);
                  }}
                  style={{
                    background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.secondaryColor || theme.primaryColor})`,
                    color: '#ffffff',
                    boxShadow: `0 8px 20px -6px ${theme.primaryColor}80`,
                  }}
                  className="group w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold text-base hover:brightness-110 hover:shadow-2xl active:scale-[0.99] transition-all"
                >
                  <span>Réserver cette formule</span>
                  <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}